
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { 
  Link2, 
  ExternalLink, 
  TrendingUp, 
  BarChart3, 
  Calendar,
  RotateCcw,
  Globe,
  Settings,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  X,
  Clock,
  Hash
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { FaviconSimple, BacklinkFavicon } from "@/components/ui/favicon";
import { preloadFavicons, preloadFaviconsWithBrandfetch } from "@/lib/favicon";
import { useSubscription } from "@/hooks/useSubscription";
import { BacklinkSettings } from "./BacklinkSettings";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Gift, Loader2 } from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { toast as sonnerToast } from "sonner";

// Types
interface WebsiteData {
  id: string;
  domain: string;
  title: string;
  favicon_url: string;
}

interface BacklinksTabProps {
  website: WebsiteData;
  user: User | null;
  isActive?: boolean;
}

interface MonthlyBatch {
  month: string; // YYYY-MM format
  monthName: string; // "January 2024"
  backlinks: Backlink[];
  count: number;
  avgDomainAuthority: number;
  uniqueDomains: number;
}

interface Backlink {
  id: string;
  source_domain: string;
  source_url: string;
  anchor_text: string;
  domain_authority: number;
  favicon_url: string;
  date_discovered: string;
  status: string;
  service: string;
}

// Metric Card Component
interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  change?: number;
  changeLabel?: string;
  showInfoIcon?: boolean;
  infoTooltip?: string;
}

const MetricCard = ({ title, value, subtitle, icon, change, changeLabel, showInfoIcon, infoTooltip }: MetricCardProps) => (
  <Card className="
    bg-background/95
    backdrop-blur-md
    border border-border/40
    shadow-[0_2px_12px_rgba(0,0,0,0.04),0_1px_4px_rgba(0,0,0,0.02)]
    hover:shadow-[0_4px_20px_rgba(0,0,0,0.06),0_2px_8px_rgba(0,0,0,0.03)]
    rounded-3xl
    transition-all duration-300 ease-out
  ">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 pt-6 px-6">
      <CardTitle className="text-sm font-semibold text-muted-foreground tracking-wide flex items-center gap-2">
        {title}
        {showInfoIcon && infoTooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs p-3 bg-background border border-border/50 shadow-lg rounded-xl">
                <p className="text-sm text-foreground font-medium leading-relaxed">{infoTooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </CardTitle>
      <div className="text-primary/80 p-3 rounded-2xl bg-primary/10 border border-primary/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">{icon}</div>
    </CardHeader>
    <CardContent className="px-6 pb-6">
      <div className="text-3xl font-bold text-foreground mb-2">{value}</div>
      {subtitle && (
        <p className="text-sm text-muted-foreground font-medium">{subtitle}</p>
      )}
      {change !== undefined && change !== null && Number(value) > 0 && (
        <div className="flex items-center space-x-2 text-sm text-muted-foreground mt-2">
          {change >= 0 ? (
            <ArrowUpRight className="h-4 w-4 text-emerald-500" />
          ) : (
            <ArrowDownRight className="h-4 w-4 text-rose-500" />
          )}
          <span className={`font-medium ${
            change >= 0 ? 'text-emerald-600' : 'text-rose-600'
          }`}>
            {Math.abs(change)}%
          </span>
          {changeLabel && <span className="text-muted-foreground">{changeLabel}</span>}
        </div>
      )}
    </CardContent>
  </Card>
);

// Header Component
interface BacklinksHeaderProps {
  loading: boolean;
  onRefresh: () => void;
  totalBacklinks: number;
  monthlyAllocation: number;
  canClaim: boolean;
  hasClaimed: boolean;
  onClaimClick: () => void;
  onSettingsClick?: () => void;
  subscriptionRenewalDate?: string | null;
}

const BacklinksHeader = ({ loading, onRefresh, totalBacklinks, monthlyAllocation, canClaim, hasClaimed, onClaimClick, onSettingsClick, subscriptionRenewalDate }: BacklinksHeaderProps) => {
  const getRenewalMessage = () => {
    if (!subscriptionRenewalDate) return "when your subscription renews";
    const renewalDate = new Date(subscriptionRenewalDate);
    const now = new Date();
    const daysUntilRenewal = Math.ceil((renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilRenewal <= 0) return "soon";
    if (daysUntilRenewal === 1) return "tomorrow";
    if (daysUntilRenewal < 7) return `in ${daysUntilRenewal} days`;
    return `on ${renewalDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  return (
    <div className="flex justify-between items-center flex-wrap gap-4">
      <div>
        <h2 className="text-3xl font-bold text-foreground tracking-tight">Backlinks</h2>
        <p className="text-muted-foreground text-lg font-medium mt-2">
          You receive {monthlyAllocation} quality backlinks every month with your subscription
          {loading && (
            <span className="ml-2 text-sm text-muted-foreground">
              (refreshing...)
            </span>
          )}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Button 
          onClick={onClaimClick}
          disabled={!canClaim || hasClaimed}
          className={`rounded-full px-6 py-3 text-base font-semibold transition-all duration-300 shadow-lg ${
            canClaim && !hasClaimed
              ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-xl"
              : "bg-muted text-muted-foreground cursor-not-allowed opacity-60"
          }`}
        >
          <Gift className="h-4 w-4 mr-2" />
          {hasClaimed ? "Claimed This Month" : "Claim Monthly Backlinks"}
        </Button>
        {hasClaimed && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs p-3 bg-background border border-border/50 shadow-lg rounded-xl">
                <p className="text-sm text-foreground font-medium leading-relaxed">
                  You can claim your next batch of backlinks {getRenewalMessage()}.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      {onSettingsClick && (
        <Button
          variant="outline"
          onClick={onSettingsClick}
          className="rounded-full px-6 py-3 text-base font-semibold border-2 border-border/50 bg-background/90 backdrop-blur-sm text-foreground hover:bg-accent hover:text-accent-foreground hover:border-border transition-all duration-500 ease-in-out shadow-sm"
        >
          <Settings className="h-4 w-4 mr-2" />
          Settings
        </Button>
      )}
      <Button 
        variant="outline" 
        className="rounded-full px-6 py-3 text-base font-semibold border-2 border-border/50 bg-background/90 backdrop-blur-sm text-foreground hover:bg-accent hover:text-accent-foreground hover:border-border transition-all duration-500 ease-in-out shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" 
        onClick={onRefresh} 
        disabled={loading}
      >
        {loading ? (
          <RotateCcw className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <RotateCcw className="h-4 w-4 mr-2" />
        )}
        {loading ? 'Refreshing...' : 'Refresh'}
      </Button>
    </div>
  </div>
  );
};

// Overview Tab Component
interface OverviewTabProps {
  backlinks: Backlink[];
  monthlyBatches: MonthlyBatch[];
  loading: boolean;
  monthlyAllocation: number;
}

const OverviewTab = ({ backlinks, monthlyBatches, loading, monthlyAllocation }: OverviewTabProps) => {
  // Calculate metrics
  const totalBacklinks = backlinks.length;
  
  // Calculate average domain authority
  const averageDomainAuthority = backlinks.length > 0
    ? Math.round(backlinks.reduce((sum, b) => sum + b.domain_authority, 0) / backlinks.length)
    : 0;

  // Calculate month-over-month change only if we have data
  const calculateChange = () => {
    if (monthlyBatches.length < 2) return null;
    
    const currentMonth = monthlyBatches[0]?.count || 0;
    const previousMonth = monthlyBatches[1]?.count || 0;
    
    if (previousMonth === 0) return null;
    
    return Math.round(((currentMonth - previousMonth) / previousMonth) * 100 * 10) / 10;
  };

  const calculateAverageDAChange = () => {
    if (monthlyBatches.length < 2) return null;
    
    const currentMonthAvg = monthlyBatches[0]?.backlinks.length > 0
      ? Math.round(monthlyBatches[0].backlinks.reduce((sum, b) => sum + b.domain_authority, 0) / monthlyBatches[0].backlinks.length)
      : 0;
    const previousMonthAvg = monthlyBatches[1]?.backlinks.length > 0
      ? Math.round(monthlyBatches[1].backlinks.reduce((sum, b) => sum + b.domain_authority, 0) / monthlyBatches[1].backlinks.length)
      : 0;
    
    if (previousMonthAvg === 0) return null;
    
    return Math.round(((currentMonthAvg - previousMonthAvg) / previousMonthAvg) * 100 * 10) / 10;
  };

  const monthOverMonthChange = calculateChange();
  const averageDAChange = calculateAverageDAChange();

  const infoTooltipText = "If you've just signed up and subscribed, please note that it can take 3-5 days for your first backlinks to appear. This is normal and your backlinks will start showing up soon!";

  return (
    <div className="space-y-8">
      {/* Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <MetricCard
          title="Total Backlinks"
          value={totalBacklinks}
          subtitle="All time"
          icon={<Link2 className="h-4 w-4" />}
          change={monthOverMonthChange ?? undefined}
          changeLabel={monthOverMonthChange !== null ? "vs last month" : undefined}
          showInfoIcon={totalBacklinks === 0}
          infoTooltip={totalBacklinks === 0 ? infoTooltipText : undefined}
        />
        
        <MetricCard
          title="Domain Authority"
          value={averageDomainAuthority}
          subtitle="Average"
          icon={<Zap className="h-4 w-4" />}
          change={averageDAChange ?? undefined}
          changeLabel={averageDAChange !== null ? "vs last month" : undefined}
        />
      </div>
    </div>
  );
};

// Monthly Batch Item Component
interface MonthlyBatchItemProps {
  batch: MonthlyBatch;
  onViewBacklinks: (batch: MonthlyBatch) => void;
}

const MonthlyBatchItem = ({ batch, onViewBacklinks }: MonthlyBatchItemProps) => {
  const getDAColor = (da: number) => {
    if (da >= 80) return 'bg-emerald-500';
    if (da >= 60) return 'bg-blue-500'; 
    if (da >= 40) return 'bg-yellow-500';
    return 'bg-gray-500';
  };

  return (
    <div className="
      flex items-center justify-between p-6 
      rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 
      shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] 
      transition-all duration-300
    ">
      <div className="flex-1 space-y-3">
        <div className="flex items-center gap-4">
          <div className="text-primary/80 p-2 rounded-2xl bg-primary/10 border border-primary/20">
            <Calendar className="h-5 w-5" />
          </div>
          <div>
            <p className="font-bold text-foreground text-lg leading-tight">
              {batch.monthName}
            </p>
            <p className="text-sm text-muted-foreground font-medium mt-1">
              {batch.count} backlinks from {batch.uniqueDomains} domains
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          <Badge variant="outline" className="rounded-full px-3 py-1.5 font-semibold text-sm bg-primary/10 border-primary/20">
            <Link2 className="h-3.5 w-3.5 mr-1.5" />
            {batch.count} links
          </Badge>
          <Badge variant="outline" className="rounded-full px-3 py-1.5 font-semibold text-sm bg-blue-500/10 border-blue-500/20 text-blue-600">
            <Globe className="h-3.5 w-3.5 mr-1.5" />
            {batch.uniqueDomains} domains
          </Badge>
          <div className={`${getDAColor(batch.avgDomainAuthority)} text-white px-3 py-1.5 rounded-full text-sm font-bold`}>
            Avg DA {batch.avgDomainAuthority}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2 ml-4">
        <Button 
          variant="outline" 
          size="sm" 
          className="rounded-full border-2"
          onClick={() => onViewBacklinks(batch)}
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          View Links
        </Button>
      </div>
    </div>
  );
};

// Monthly Batches Tab Component
interface MonthlyBatchesTabProps {
  monthlyBatches: MonthlyBatch[];
  loading: boolean;
  onViewBacklinks: (batch: MonthlyBatch) => void;
}

const MonthlyBatchesTab = ({ monthlyBatches, loading, onViewBacklinks }: MonthlyBatchesTabProps) => (
  <div className="space-y-6">
    <Card className="
      bg-background/95
      backdrop-blur-lg
      border border-border/30
      shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
      hover:shadow-[0_6px_24px_rgba(0,0,0,0.06),0_2px_10px_rgba(0,0,0,0.03)]
      rounded-3xl
      transition-all duration-500 ease-out
    ">
      <CardHeader className="pb-6 pt-8 px-8">
        <CardTitle className="text-2xl font-bold text-foreground tracking-tight">Monthly Backlink Batches</CardTitle>
      </CardHeader>
      <CardContent className="px-8 pb-8">
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20">
                <div className="flex-1 space-y-3">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-6 w-20" />
                </div>
                <Skeleton className="h-10 w-24 rounded-full" />
              </div>
            ))}
          </div>
        ) : monthlyBatches.length === 0 ? (
          <div className="text-center py-12">
            <div className="p-4 rounded-3xl bg-muted/20 inline-block mb-4">
              <Calendar className="h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">No Monthly Batches Yet</h3>
            <p className="text-muted-foreground font-medium">
              Your monthly backlinks will appear here as they're delivered
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {monthlyBatches.map((batch) => (
              <MonthlyBatchItem 
                key={batch.month} 
                batch={batch} 
                onViewBacklinks={onViewBacklinks}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);

// Backlink Item Component
interface BacklinkItemProps {
  backlink: Backlink;
}

const BacklinkItem = ({ backlink }: BacklinkItemProps) => {
  const getDAColor = (da: number) => {
    if (da >= 80) return 'bg-emerald-500';
    if (da >= 60) return 'bg-blue-500'; 
    if (da >= 40) return 'bg-yellow-500';
    return 'bg-gray-500';
  };

  return (
    <div className="
      flex items-center justify-between p-6 
      rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 
      shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] 
      transition-all duration-300
    ">
      <div className="flex-1 space-y-4">
        <div className="flex items-center gap-4">
          <BacklinkFavicon 
            domain={backlink.source_domain}
            size={40}
            className="w-10 h-10 rounded-xl border border-border/30"
          />
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="font-bold text-foreground text-lg leading-tight">{backlink.source_domain}</p>
              <div className={`${getDAColor(backlink.domain_authority)} text-white px-3 py-1 rounded-full text-sm font-bold`}>
                DA {backlink.domain_authority}
              </div>
            </div>
            <p className="text-sm text-muted-foreground font-medium mt-1">{backlink.service}</p>
          </div>
        </div>
        
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground font-medium">
            <span className="font-bold text-foreground">"{backlink.anchor_text}"</span>
          </p>
          <p className="text-xs text-muted-foreground font-medium truncate max-w-md">
            {backlink.source_url}
          </p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          <Badge variant={backlink.status === 'active' ? 'default' : 'destructive'} className="text-xs rounded-full px-3 py-1 font-semibold">
            {backlink.status}
          </Badge>
          <span className="text-xs text-muted-foreground font-medium">
            {new Date(backlink.date_discovered).toLocaleDateString()}
          </span>
        </div>
      </div>
      
      <div className="flex items-center gap-2 ml-4">
        <Button variant="outline" size="sm" asChild className="rounded-full border-2">
          <a href={backlink.source_url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-4 w-4 mr-2" />
            Visit
          </a>
        </Button>
      </div>
    </div>
  );
};

// Backlinks Tab Component
interface BacklinksListTabProps {
  backlinks: Backlink[];
  loading: boolean;
}

const BacklinksListTab = ({ backlinks, loading }: BacklinksListTabProps) => (
  <div className="space-y-6">
    <Card className="
      bg-background/95
      backdrop-blur-lg
      border border-border/30
      shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
      hover:shadow-[0_6px_24px_rgba(0,0,0,0.06),0_2px_10px_rgba(0,0,0,0.03)]
      rounded-3xl
      transition-all duration-500 ease-out
    ">
      <CardHeader className="pb-6 pt-8 px-8">
        <CardTitle className="text-2xl font-bold text-foreground tracking-tight">All Backlinks</CardTitle>
      </CardHeader>
      <CardContent className="px-8 pb-8">
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20">
                <div className="flex-1 space-y-3">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-6 w-20" />
                </div>
                <Skeleton className="h-10 w-24 rounded-full" />
              </div>
            ))}
          </div>
        ) : backlinks.length === 0 ? (
          <div className="text-center py-12">
            <div className="p-4 rounded-3xl bg-muted/20 inline-block mb-4">
              <Link2 className="h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">No Backlinks Yet</h3>
            <p className="text-muted-foreground font-medium">
              Your monthly backlinks will appear here as they're delivered with your subscription
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {backlinks.map((backlink) => (
              <BacklinkItem key={backlink.id} backlink={backlink} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);


// DA 70+ Service ID - Hardcoded to ensure only this service is shown
// Service: "PR9 - DA (Domain Authority) 70+" (ID: 19, Code: DA70, Price: 25.000)
// If the service ID changes in the future, update this value
const DA_70_SERVICE_ID = "19";

// Helper function to simplify service display names for better UX
// This only affects display - the actual service_id used for API calls remains unchanged
const simplifyServiceName = (service: any): string => {
  const name = service.name || service.description || '';
  
  // If already short, return as-is
  if (name.length <= 30) {
    return name;
  }
  
  // Common simplification patterns
  // "DA (Domain Authority) 50+ Do-follow" → "DA 50+"
  if (name.includes('DA') || name.includes('Domain Authority')) {
    const daMatch = name.match(/DA\s*\(?Domain Authority\)?\s*(\d+)\+?/i);
    if (daMatch) {
      return `DA ${daMatch[1]}+`;
    }
    // Fallback: extract DA number
    const daNumMatch = name.match(/(\d+)\+?\s*Do-follow/i);
    if (daNumMatch) {
      return `DA ${daNumMatch[1]}+`;
    }
  }
  
  // "High Authority Backlinks" → "High Authority"
  if (name.toLowerCase().includes('high authority')) {
    return 'High Authority';
  }
  
  // "Premium Backlinks Package" → "Premium"
  if (name.toLowerCase().includes('premium')) {
    return 'Premium';
  }
  
  // "Standard Backlinks" → "Standard"
  if (name.toLowerCase().includes('standard')) {
    return 'Standard';
  }
  
  // Generic: take first meaningful words (up to 3 words, max 30 chars)
  const words = name.split(/\s+/).filter(w => 
    w.length > 0 && 
    !w.match(/^(backlinks?|package|service|link)$/i)
  );
  
  if (words.length > 0) {
    let simplified = '';
    for (const word of words.slice(0, 3)) {
      if ((simplified + ' ' + word).length <= 30) {
        simplified = simplified ? simplified + ' ' + word : word;
      } else {
        break;
      }
    }
    if (simplified) return simplified;
  }
  
  // Fallback: truncate with ellipsis
  return name.substring(0, 27) + '...';
};

// Main BacklinksTab Component
export function BacklinksTab({ website, user, isActive = true }: BacklinksTabProps) {
  const { toast } = useToast();
  const { checkBenefitLimit, incrementBenefitUsage, hasActiveSubscription, subscription, benefits } = useSubscription(user);
  const [backlinks, setBacklinks] = useState<Backlink[]>([]);
  const [monthlyBatches, setMonthlyBatches] = useState<MonthlyBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState<MonthlyBatch | null>(null);
  const [showFirstTimePopup, setShowFirstTimePopup] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [hasClaimedThisMonth, setHasClaimedThisMonth] = useState(false);
  const [pendingOrders, setPendingOrders] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  // Monthly allocation - could be fetched from user's subscription settings
  const monthlyAllocation = 10;
  
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [keywords, setKeywords] = useState<string[]>(['', '', '']);
  const [quantity] = useState<number>(monthlyAllocation); // Auto-set to 10, no need to change
  const [loadingServices, setLoadingServices] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const backlinkLimit = checkBenefitLimit('backlinks');

  // Check if user has claimed backlinks this month and load pending orders
  useEffect(() => {
    if (user && website?.id) {
      checkClaimedThisMonth();
      loadPendingOrders();
    }
  }, [user, website?.id]);

  // Set up real-time subscriptions for automatic order status updates and new backlinks
  // This ensures the UI updates automatically when:
  // 1. Order status changes (pending -> processing -> completed)
  // 2. New backlinks are added to the database
  // No manual refresh needed - everything updates in real-time!
  useEffect(() => {
    if (!website?.id || !isActive) return;

    console.log('🔔 Setting up real-time subscriptions for backlinks and orders');

    // Subscribe to seo_orders table changes (order status updates)
    const ordersChannel = supabase
      .channel(`seo_orders_${website.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'seo_orders',
          filter: `website_id=eq.${website.id}`,
        },
        (payload) => {
          console.log('📦 Order status updated via real-time:', payload);
          // Refresh pending orders when any order changes
          loadPendingOrders();
          // Also refresh claimed status in case a new order was created
          checkClaimedThisMonth();
          
          // If order was completed, refresh backlinks data
          if (payload.eventType === 'UPDATE' && payload.new?.status === 'completed') {
            console.log('✅ Order completed, refreshing backlinks...');
            loadBacklinksData();
            toast({
              title: "Order Completed!",
              description: "Your backlinks are being processed and will appear shortly.",
              variant: "default"
            });
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 Orders real-time subscription status:', status);
      });

    // Subscribe to backlinks table changes (new backlinks added)
    const backlinksChannel = supabase
      .channel(`backlinks_${website.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT', // Only listen to new backlinks being added
          schema: 'public',
          table: 'backlinks',
          filter: `website_id=eq.${website.id}`,
        },
        (payload) => {
          console.log('🔗 New backlink added via real-time:', payload);
          // Refresh backlinks data when new backlinks are added
          loadBacklinksData();
        }
      )
      .subscribe((status) => {
        console.log('📡 Backlinks real-time subscription status:', status);
      });

    // Cleanup subscriptions when component unmounts or website changes
    return () => {
      console.log('🧹 Cleaning up real-time subscriptions');
      supabase.removeChannel(ordersChannel);
      supabase.removeChannel(backlinksChannel);
    };
    // Note: loadPendingOrders, checkClaimedThisMonth, loadBacklinksData, and toast are stable
    // functions/hooks that don't need to be in dependencies
  }, [website?.id, isActive]);

  const loadPendingOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('seo_orders')
        .select('*')
        .eq('website_id', website.id)
        .in('status', ['pending', 'processing'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPendingOrders(data || []);
    } catch (error) {
      console.error('Error loading pending orders:', error);
    }
  };

  const checkClaimedThisMonth = async () => {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const { data, error } = await supabase
        .from('seo_orders')
        .select('id')
        .eq('website_id', website.id)
        .gte('created_at', startOfMonth.toISOString())
        .limit(1);

      if (error) throw error;
      
      setHasClaimedThisMonth((data?.length || 0) > 0);
    } catch (error) {
      console.error('Error checking claimed status:', error);
    }
  };

  const loadServices = async () => {
    try {
      setLoadingServices(true);
      
      // Auto-select DA 70+ service (ID: 19) - no need to load services since it's hardcoded
      setSelectedServiceId(DA_70_SERVICE_ID || "19");
      
      // Load user's saved keywords
      if (user) {
        const { data: settings } = await supabase
          .from('backlink_settings')
          .select('keywords')
          .eq('user_id', user.id)
          .eq('website_id', website.id)
          .maybeSingle();

        if (settings && settings.keywords && settings.keywords.length > 0) {
          const paddedKeywords = [...settings.keywords, '', '', ''].slice(0, 3);
          setKeywords(paddedKeywords);
        }
      }
    } catch (error: any) {
      console.error('Error loading settings:', error);
    } finally {
      setLoadingServices(false);
    }
  };

  const handleClaimClick = () => {
    // Check if user has remaining backlinks
    if (!backlinkLimit.allowed) {
      toast({
        title: "Limit Reached",
        description: "You've used all your monthly backlinks. They reset on your subscription renewal.",
        variant: "destructive"
      });
      return;
    }

    // Quantity is auto-set to monthly allocation (10)

    // Check if keywords are set (first time)
    if (user) {
      supabase
        .from('backlink_settings')
        .select('keywords')
        .eq('user_id', user.id)
        .eq('website_id', website.id)
        .maybeSingle()
        .then(({ data }) => {
          if (!data || !data.keywords || data.keywords.length === 0) {
            // First time - show popup and require keywords
            setShowFirstTimePopup(true);
          } else {
            // Not first time - open claim modal
            setShowClaimModal(true);
            loadServices();
          }
        });
    } else {
      setShowClaimModal(true);
      loadServices();
    }
  };

  const handleFirstTimePopupClose = () => {
    setShowFirstTimePopup(false);
    // Quantity is auto-set to monthly allocation (10)
    setShowClaimModal(true);
    loadServices();
  };

  const handleClaimBacklinks = async () => {
    if (!user || !website) return;

    // Service is auto-selected (DA 70+), ensure it's set
    const serviceId = selectedServiceId || DA_70_SERVICE_ID || "19";
    if (!serviceId) {
      toast({
        title: "Error",
        description: "Unable to determine backlink service",
        variant: "destructive"
      });
      return;
    }

    // Validate keywords (at least one required)
    const validKeywords = keywords.filter(k => k.trim().length > 0);
    if (validKeywords.length === 0) {
      toast({
        title: "Keywords Required",
        description: "Please enter at least one keyword",
        variant: "destructive"
      });
      return;
    }

    try {
      setClaiming(true);

      // Save keywords if this is first time
      const { data: existingSettings } = await supabase
        .from('backlink_settings')
        .select('id')
        .eq('user_id', user.id)
        .eq('website_id', website.id)
        .maybeSingle();

      if (!existingSettings || !existingSettings.id) {
        await supabase
          .from('backlink_settings')
          .upsert({
            user_id: user.id,
            website_id: website.id,
            keywords: validKeywords,
            preferred_service_id: serviceId,
            updated_at: new Date().toISOString(),
          }, {
            onConflict: 'user_id,website_id'
          });
      }

      // Create order via SEO panel API
      const targetUrl = website.domain.startsWith('http') ? website.domain : `https://${website.domain}`;
      const keywordsString = validKeywords.join(', ');

      const { data: orderData, error: orderError } = await supabase.functions.invoke('seo-panel-api', {
        body: {
          action: 'order',
          service_id: serviceId,
          quantity: quantity,
          links: targetUrl,
          keywords: keywordsString,
          website_id: website.id,
        }
      });

      if (orderError) throw orderError;

      // Increment benefit usage by quantity claimed
      try {
        // Get fresh benefit data from database
        const { data: subscriptionData } = await supabase
          .from('subscriptions')
          .select('id')
          .eq('user_id', user.id)
          .in('status', ['active', 'trialing'])
          .maybeSingle();

        if (subscriptionData) {
          const { data: benefitData, error: benefitFetchError } = await supabase
            .from('subscription_benefits')
            .select('id, used_count, benefit_limit')
            .eq('subscription_id', subscriptionData.id)
            .eq('benefit_type', 'backlinks')
            .single();

          if (!benefitFetchError && benefitData) {
            const currentUsed = benefitData.used_count || 0;
            const newUsedCount = Math.min(
              currentUsed + quantity,
              benefitData.benefit_limit || Infinity
            );
            
            const { error: updateError } = await supabase
              .from('subscription_benefits')
              .update({
                used_count: newUsedCount,
                updated_at: new Date().toISOString(),
              })
              .eq('id', benefitData.id);

            if (updateError) {
              console.error('Error updating benefit usage:', updateError);
              throw updateError;
            } else {
              console.log(`✅ Updated backlinks usage: ${currentUsed} → ${newUsedCount} (quantity: ${quantity})`);
              // Force refresh subscription benefits in the hook
              // The real-time subscription should pick this up, but trigger a manual refresh too
              if (subscription?.id) {
                // Dispatch event to trigger refresh in useSubscription hook
                window.dispatchEvent(new CustomEvent('subscription-benefit-updated', { 
                  detail: { subscriptionId: subscription.id, benefitType: 'backlinks' } 
                }));
              }
            }
          } else {
            console.error('Error fetching benefit:', benefitFetchError);
            // Fallback: try hook method
            await incrementBenefitUsage('backlinks');
          }
        } else {
          console.error('No active subscription found');
        }
      } catch (usageError: any) {
        console.error('Error incrementing usage:', usageError);
        // Final fallback: try hook method (increments by 1)
        try {
          await incrementBenefitUsage('backlinks');
          console.log('⚠️ Used fallback increment (by 1)');
        } catch (e) {
          console.error('Fallback increment also failed:', e);
        }
      }

      // Mark as claimed
      setHasClaimedThisMonth(true);
      
      // Show success animation
      setShowSuccessAnimation(true);
      setShowClaimModal(false);
      
      // Hide animation after 3 seconds
      setTimeout(() => {
        setShowSuccessAnimation(false);
      }, 3000);

      // Refresh data
      await checkClaimedThisMonth();
      await loadPendingOrders();
      await loadBacklinksData();

      sonnerToast.success('Backlinks claimed successfully!', {
        description: 'Your backlinks will be delivered within 3-5 days.',
        duration: 5000,
      });

    } catch (error: any) {
      console.error('Error claiming backlinks:', error);
      toast({
        title: "Error",
        description: error?.message || "Failed to claim backlinks. Please try again.",
        variant: "destructive"
      });
    } finally {
      setClaiming(false);
    }
  };

  // Helper function to get realistic domain authority based on domain
  const getDomainAuthority = (domain: string): number => {
    const domainAuthorities: { [key: string]: number } = {
      'reddit.com': 95,
      'quora.com': 92,
      'medium.com': 96,
      'linkedin.com': 99,
      'github.com': 96,
      'stackoverflow.com': 87,
      'issuu.com': 93,
      'myspace.com': 83,
      'intensedebate.com': 48,
      'sbnation.com': 69,
      'canvas.instructure.com': 78,
      'output.jsbin.com': 47,
      'mystrikingly.com': 55,
      'footballzaa.com': 42,
      'bitsdujour.com': 63,
      '500px.com': 80,
      'u.to': 35,
      'efficientmx.com': 25,
      'yahoo.com': 95
    };
    
    if (domainAuthorities[domain]) {
      return domainAuthorities[domain];
    }
    
    for (const [knownDomain, da] of Object.entries(domainAuthorities)) {
      if (domain.includes(knownDomain)) {
        return da;
      }
    }
    
    if (domain.includes('.edu')) return Math.floor(Math.random() * 15) + 70;
    if (domain.includes('.gov')) return Math.floor(Math.random() * 20) + 75;
    if (domain.includes('.org')) return Math.floor(Math.random() * 25) + 50;
    
    return Math.floor(Math.random() * 40) + 20;
  };
  
  const getFaviconUrl = (domain: string): string => {
    return `https://cdn.brandfetch.io/${domain}/w/128/h/128?c=1iddr807FNSCfhcKGsr`;
  };

  // Helper function to group backlinks by month
  const groupBacklinksByMonth = (backlinks: Backlink[]): MonthlyBatch[] => {
    const monthGroups: { [key: string]: Backlink[] } = {};
    
    backlinks.forEach(backlink => {
      const date = new Date(backlink.date_discovered);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthGroups[monthKey]) {
        monthGroups[monthKey] = [];
      }
      monthGroups[monthKey].push(backlink);
    });

    return Object.entries(monthGroups)
      .map(([month, monthBacklinks]) => {
        const [year, monthNum] = month.split('-');
        const monthName = new Date(parseInt(year), parseInt(monthNum) - 1).toLocaleDateString('en-US', { 
          month: 'long', 
          year: 'numeric' 
        });
        
        const uniqueDomains = new Set(monthBacklinks.map(b => b.source_domain)).size;
        const avgDomainAuthority = monthBacklinks.length > 0
          ? Math.round(monthBacklinks.reduce((sum, b) => sum + b.domain_authority, 0) / monthBacklinks.length)
          : 0;

        return {
          month,
          monthName,
          backlinks: monthBacklinks,
          count: monthBacklinks.length,
          avgDomainAuthority,
          uniqueDomains
        };
      })
      .sort((a, b) => b.month.localeCompare(a.month)); // Sort by month descending (newest first)
  };

  // Track if data has been loaded initially for current website
  const [loadedWebsiteId, setLoadedWebsiteId] = useState<string | null>(null);

  useEffect(() => {
    // Only load data if tab is active
    if (!isActive) return;
    
    // Reset loaded state if website changed
    if (website?.id !== loadedWebsiteId) {
      setLoadedWebsiteId(null);
    }
    
    if (website?.id && website.id !== loadedWebsiteId) {
      loadBacklinksData();
      setLoadedWebsiteId(website.id);
    }
  }, [website?.id, isActive, loadedWebsiteId]);

  const loadBacklinksData = async () => {
    try {
      setLoading(true);
      
      // Load direct backlinks from the backlinks table
      const { data: directBacklinks, error: directError } = await supabase
        .from('backlinks')
        .select('*')
        .eq('website_id', website.id)
        .order('placed_at', { ascending: false });

      // Load backlinks from seo_reports (existing system)
      const { data: reportsData, error: reportsError } = await supabase
        .from('seo_reports')
        .select(`
          id,
          processed_data,
          created_at,
          seo_order_id,
          seo_orders!inner (
            website_id,
            service_description
          )
        `)
        .eq('seo_orders.website_id', website.id);

      const extractedBacklinks: Backlink[] = [];
      const seenUrls = new Set<string>();

      // Process direct backlinks
      if (directBacklinks && !directError) {
        directBacklinks.forEach(backlink => {
          if (seenUrls.has(backlink.source_url)) return;
          seenUrls.add(backlink.source_url);
          
          extractedBacklinks.push({
            id: backlink.id,
            source_domain: backlink.source_domain,
            source_url: backlink.source_url,
            anchor_text: backlink.anchor_text || 'Link',
            domain_authority: backlink.domain_rating || getDomainAuthority(backlink.source_domain),
            favicon_url: getFaviconUrl(backlink.source_domain),
            date_discovered: backlink.placed_at,
            status: backlink.status,
            service: 'Monthly Subscription'
          });
        });
      }

      // Process backlinks from reports (legacy)
      if (reportsData && !reportsError) {
        reportsData.forEach(report => {
          const processedData = report.processed_data as any;
          if (processedData?.urls && Array.isArray(processedData.urls)) {
            processedData.urls.forEach((url: string, index: number) => {
              if (seenUrls.has(url)) return;
              seenUrls.add(url);
              
              try {
                const domain = new URL(url).hostname;
                const anchor = processedData.anchors?.[index] || 'Link';
                
                if (anchor === 'anchorText' || anchor === 'linkOnPage' || 
                    anchor.includes('@') || anchor.includes('password') ||
                    anchor.length < 3 || anchor.length > 50) {
                  return;
                }
                
                extractedBacklinks.push({
                  id: `${report.id}-${index}`,
                  source_domain: domain,
                  source_url: url,
                  anchor_text: anchor,
                  domain_authority: getDomainAuthority(domain),
                  favicon_url: getFaviconUrl(domain),
                  date_discovered: report.created_at,
                  status: 'active',
                  service: report.seo_orders?.service_description || 'Legacy Campaign'
                });
              } catch (e) {
                console.warn('Invalid URL skipped:', url);
              }
            });
          }
        });
      }
      
      // Set backlinks and group by month
      setBacklinks(extractedBacklinks);
      setMonthlyBatches(groupBacklinksByMonth(extractedBacklinks));
      
      // Preload favicons
      const domains = extractedBacklinks.map(b => b.source_domain);
      preloadFaviconsWithBrandfetch(domains).catch(console.warn);
      
      // Refresh pending orders when backlinks are loaded
      if (user && website?.id) {
        loadPendingOrders();
      }

    } catch (error) {
      console.error('Error loading backlinks data:', error);
      toast({
        title: "Error",
        description: "Failed to load backlinks data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewBacklinks = (batch: MonthlyBatch) => {
    setSelectedBatch(batch);
  };

  return (
    <>
      {/* Success Animation */}
      {showSuccessAnimation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="animate-in fade-in zoom-in duration-300">
            <div className="bg-background rounded-3xl p-12 shadow-2xl border-2 border-primary/20 flex flex-col items-center gap-6">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
                <CheckCircle2 className="h-20 w-20 text-primary relative z-10" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-2xl font-bold text-foreground">Backlinks Claimed!</h3>
                <p className="text-muted-foreground text-lg">
                  Your monthly backlinks are being processed
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* First-time popup - now shown when claiming */}
      <Dialog open={showFirstTimePopup} onOpenChange={setShowFirstTimePopup}>
        <DialogContent className="sm:max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
              <Info className="h-5 w-5 text-primary" />
              First Time Claiming Backlinks
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2 leading-relaxed">
              Please note that it can take 3-5 days for your backlinks to appear. This is normal and your backlinks will start showing up soon!
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mt-4">
            <Button 
              onClick={handleFirstTimePopupClose}
              className="rounded-full px-6"
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Claim Modal */}
      <Dialog open={showClaimModal} onOpenChange={setShowClaimModal}>
        <DialogContent className="sm:max-w-2xl rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              Claim Monthly Backlinks
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            {/* Top Row: Service Info + Quantity - Horizontal Layout */}
            <div className="grid grid-cols-2 gap-4">
              {/* Service Info Card */}
              <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-primary/10 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center border border-primary/30">
                    <Link2 className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">Premium Backlinks</p>
                    <p className="text-xs text-muted-foreground">High Authority (DA 70+)</p>
                  </div>
                  <Badge variant="secondary" className="rounded-full text-xs px-2 py-1 bg-primary/10 text-primary border border-primary/20">
                    Auto
                  </Badge>
                </div>
              </Card>

              {/* Quantity Info */}
              <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-primary/10 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center border border-primary/30">
                    <Hash className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">You will redeem</p>
                    <p className="text-xs text-muted-foreground">{monthlyAllocation} backlinks</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Keywords - Horizontal Layout */}
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium text-foreground">
                  Target Keywords <span className="text-muted-foreground font-normal">(at least one)</span>
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Enter keywords that will be used for your backlinks. These help determine where and how your backlinks are placed.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[0, 1, 2].map((index) => (
                  <div key={index} className="space-y-1">
                    <Label htmlFor={`claim-keyword-${index}`} className="text-xs text-muted-foreground">
                      {index === 0 ? 'Primary' : `Keyword ${index + 1}`}
                    </Label>
                    <Input
                      id={`claim-keyword-${index}`}
                      type="text"
                      value={keywords[index] || ''}
                      onChange={(e) => {
                        const newKeywords = [...keywords];
                        newKeywords[index] = e.target.value;
                        setKeywords(newKeywords);
                      }}
                      placeholder={index === 0 ? "Required" : "Optional"}
                      className={`rounded-lg h-10 text-sm ${
                        index === 0 
                          ? 'border-primary/30 focus:border-primary/50' 
                          : ''
                      }`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-border/50">
              <Button
                variant="outline"
                onClick={() => setShowClaimModal(false)}
                className="rounded-lg"
                disabled={claiming}
              >
                Cancel
              </Button>
              <Button
                onClick={handleClaimBacklinks}
                className="rounded-lg"
                disabled={claiming || !(selectedServiceId || DA_70_SERVICE_ID) || keywords.filter(k => k.trim().length > 0).length === 0}
              >
                {claiming ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Claiming...
                  </>
                ) : (
                  <>
                    <Gift className="h-4 w-4 mr-2" />
                    Claim
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-8">
        {/* Header Section */}
        <BacklinksHeader 
          loading={loading}
          onRefresh={loadBacklinksData}
          totalBacklinks={backlinks.length}
          monthlyAllocation={monthlyAllocation}
          canClaim={hasActiveSubscription && backlinkLimit.allowed}
          hasClaimed={hasClaimedThisMonth}
          onClaimClick={handleClaimClick}
          onSettingsClick={user ? () => setShowSettings(true) : undefined}
          subscriptionRenewalDate={subscription?.current_period_end || null}
        />

        {/* Backlink Settings Modal */}
        {user && (
          <BacklinkSettings
            user={user}
            website={website}
            open={showSettings}
            onOpenChange={setShowSettings}
            onSettingsUpdated={() => {
              setShowSettings(false);
              loadServices();
            }}
          />
        )}

        {/* Pending Orders Status */}
        {pendingOrders.length > 0 && (
          <Card className="bg-background/95 backdrop-blur-md border border-border/40 rounded-3xl">
            <CardHeader className="pb-4 pt-6 px-6">
              <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Pending Backlink Orders
              </CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              <div className="space-y-3">
                {pendingOrders.map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-4 rounded-2xl bg-muted/30 border border-border/20"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <Badge
                          variant={order.status === 'processing' ? 'default' : 'outline'}
                          className="rounded-full text-xs font-semibold"
                        >
                          {order.status === 'pending' ? 'Pending' : 'Processing'}
                        </Badge>
                        <span className="text-sm font-medium text-foreground">
                          Backlinks
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {order.quantity} backlinks • Order #{order.order_id} • {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground mt-2">
                  Your backlinks are being processed and will appear here once completed (usually 3-5 days).
                </p>
              </div>
            </CardContent>
          </Card>
        )}

      {/* Overview Metrics Section */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="text-primary/80 p-2 rounded-2xl bg-primary/10 border border-primary/20">
            <BarChart3 className="h-5 w-5" />
          </div>
          <h2 className="text-2xl font-bold text-foreground tracking-tight">Overview</h2>
        </div>
        <OverviewTab 
          backlinks={backlinks}
          monthlyBatches={monthlyBatches}
          loading={loading}
          monthlyAllocation={monthlyAllocation}
        />
      </div>

      {/* Monthly Batches Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-primary/80 p-2 rounded-2xl bg-primary/10 border border-primary/20">
              <Calendar className="h-5 w-5" />
            </div>
            <h2 className="text-2xl font-bold text-foreground tracking-tight">Monthly Batches</h2>
          </div>
          <Badge variant="outline" className="rounded-full px-3 py-1.5 font-semibold text-sm bg-primary/10 text-primary border-primary/20">
            {monthlyBatches.length} months
          </Badge>
        </div>
        <MonthlyBatchesTab 
          monthlyBatches={monthlyBatches}
          loading={loading}
          onViewBacklinks={handleViewBacklinks}
        />
      </div>

      {/* All Backlinks Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="text-primary/80 p-2 rounded-2xl bg-primary/10 border border-primary/20">
              <Link2 className="h-5 w-5" />
            </div>
            <h2 className="text-2xl font-bold text-foreground tracking-tight">
              {selectedBatch ? `${selectedBatch.monthName} Backlinks` : 'All Backlinks'}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {selectedBatch && (
              <Button 
                variant="outline" 
                onClick={() => setSelectedBatch(null)}
                className="rounded-full border-2"
                size="sm"
              >
                View All Backlinks
              </Button>
            )}
            <Badge variant="outline" className="rounded-full px-3 py-1.5 font-semibold text-sm bg-blue-500/10 text-blue-600 border-blue-500/20">
              {selectedBatch ? selectedBatch.backlinks.length : backlinks.length} links
            </Badge>
          </div>
        </div>
        <BacklinksListTab 
          backlinks={selectedBatch ? selectedBatch.backlinks : backlinks}
          loading={loading}
        />
      </div>
      </div>
    </>
  );
}
